import { useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, Loader2, Send } from "lucide-react";

/**
 * Reusable composer for a new root note or an inline reply.
 * Split into its own file so the parent module doesn't self-recurse
 * through JSX (which causes CRA's babel-loader to hit stack limits).
 */
export default function NoteComposer({
  placeholder, onSubmit, onCancel, disabled, autoFocus, value, onChange, testIdPrefix,
}) {
  const [internalBody, setInternalBody] = useState("");
  const [files, setFiles] = useState([]);
  const body = value !== undefined ? value : internalBody;
  const setBody = onChange || setInternalBody;

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    const ok = await onSubmit(trimmed, files);
    if (ok !== false) {
      setBody("");
      setFiles([]);
    }
  };

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const okFiles = picked.filter((f) => {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(f.name + " exceeds 5 MB limit");
        return false;
      }
      return true;
    });
    setFiles((prev) => [...prev, ...okFiles]);
    e.target.value = "";
  };

  return (
    <div className="bg-[#0D1424] border border-white/10 p-4">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        rows={3}
        autoFocus={autoFocus}
        disabled={disabled}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); handleSubmit(); }
        }}
        className="w-full bg-black/40 border border-white/10 focus:border-cyan-400/60 outline-none text-sm text-slate-200 p-3 min-h-[70px] disabled:opacity-50"
        data-testid={testIdPrefix + "-textarea"}
      />

      {files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={testIdPrefix + "-pending-files"}>
          {files.map((f, i) => (
            <div key={i} className="border border-white/10 px-2 py-1 text-xs text-slate-400 inline-flex items-center gap-2">
              <Paperclip className="w-3 h-3" />
              <span className="truncate max-w-[220px]">{f.name}</span>
              <span className="text-slate-600">{Math.round(f.size / 1024)} KB</span>
              <button
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                className="text-slate-500 hover:text-red-400"
                aria-label="Remove file"
              ><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center gap-3">
        <label className="border border-white/10 hover:border-cyan-400/40 px-3 py-1.5 text-xs mono-label text-slate-400 hover:text-cyan-400 cursor-pointer inline-flex items-center gap-2">
          <Paperclip className="w-3.5 h-3.5" />ATTACH
          <input
            type="file"
            multiple
            className="hidden"
            onChange={pickFiles}
            data-testid={testIdPrefix + "-file-input"}
          />
        </label>
        <button
          onClick={handleSubmit}
          disabled={disabled || !body.trim()}
          className="bf-pill px-4 py-1.5 text-xs tracking-widest uppercase font-display font-bold inline-flex items-center gap-2 disabled:opacity-50 ml-auto"
          data-testid={testIdPrefix + "-submit"}
        >
          {disabled ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {onCancel ? "REPLY" : "POST"}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="border border-white/10 hover:border-white/30 px-4 py-1.5 text-xs tracking-widest uppercase font-display font-bold"
            data-testid={testIdPrefix + "-cancel"}
          >CANCEL</button>
        )}
      </div>

      <div className="mt-2 text-[10px] text-slate-600">Ctrl/⌘ + Enter to post</div>
    </div>
  );
}
